<script lang="ts">
	import {
		computePosition,
		autoUpdate,
		offset,
		shift,
		flip,
		type Placement
	} from '@floating-ui/dom';
	import { onDestroy } from 'svelte';

	interface Props {
		open?: boolean;
		placement?: Placement;
		triggerEl?: HTMLElement | null;
		offsetVal?: number;
		children?: import('svelte').Snippet;
	}

	let {
		open = $bindable(false),
		placement = 'bottom',
		triggerEl = null,
		offsetVal = 8,
		children
	}: Props = $props();

	let popupEl: HTMLElement = $state()!;
	// `cleanup` is a side-effect handle — never read in template, never used reactively.
	// Wrapping it in `$state` made the $effect below depend on its own write and
	// triggered `effect_update_depth_exceeded` at runtime (legacy_recursive_reactive_block).
	let cleanup: (() => void) | null = null;

	// Portal action: move the node out of any parent layout context (flex, transform,
	// contain, etc.) into document.body so position:fixed truly resolves to viewport
	// and the trigger's parent flex row doesn't reflow when the popup mounts.
	function portal(node: HTMLElement) {
		document.body.appendChild(node);
		return {
			destroy() {
				node.remove();
			}
		};
	}

	// DOM positioning side effect (per PATTERNS § $:-side-effect-callout): MUST be
	// $effect, NOT $derived.
	$effect(() => {
		if (open && triggerEl && popupEl) {
			cleanup?.();
			cleanup = autoUpdate(triggerEl, popupEl, () => {
				computePosition(triggerEl as HTMLElement, popupEl, {
					placement,
					strategy: 'fixed',
					middleware: [offset(offsetVal), flip(), shift({ padding: 8 })]
				}).then(({ x, y }) => {
					Object.assign(popupEl.style, { left: `${x}px`, top: `${y}px` });
				});
			});
		} else if (!open) {
			cleanup?.();
			cleanup = null;
		}
	});

	onDestroy(() => cleanup?.());
</script>

{#if open}
	<!-- `fixed` (not `absolute`) so the popup sits in the viewport coordinate space
	     @floating-ui's computePosition() returns; `absolute` resolves against the
	     nearest positioned ancestor and was causing surrounding content to shift on
	     first paint before Floating UI re-positioned. -->
	<div use:portal bind:this={popupEl} class="fixed top-0 left-0 z-[999]" role="tooltip">
		{@render children?.()}
	</div>
{/if}
